/* Runtime config: resolved at app boot from window-injected JSON or env vars.
 * Mirrors rpapp-admin/rpapp-kiosk runtime-config pattern so Railway can
 * override API base URL without rebuild.
 */

import {
  type CustomerFeatureFlags,
  resolveCustomerFeatureFlags,
} from './customerFeatureFlags.js';
import { parseCustomerBootFlagEnv } from './parseCustomerBootFlagEnv.js';
import { parseCustomerBrowseNavBootEnv } from './parseCustomerBrowseNavBootEnv.js';

export interface RuntimeConfig {
  apiBaseUrl: string;
  passkeyLoginEnabled: boolean;
  buildVersion: string;
  /** When true, FE gates non-allowlisted events without analytics cookie consent. */
  consentIngestGateEnabled: boolean;
  /** Proactive `POST /customer-auth/refresh` interval (ms); 0 disables. */
  sessionRefreshIntervalMs: number;
  /** W4 §9.2 — idle inactivity logout (ms). */
  customerIdleTimeoutMs: number;
  /** W4 §9.2 — absolute session wall-clock max (ms). */
  customerAbsoluteSessionMaxMs: number;
  sentryDsn?: string;
  sentryEnvironment?: string;
  /** Customer IA / visual rollout flags — default OFF. */
  featureFlags: CustomerFeatureFlags;
}

interface WindowWithRuntimeConfig extends Window {
  __RPAPP_CUSTOMER_CONFIG__?: Partial<RuntimeConfig> & {
    featureFlags?: Partial<CustomerFeatureFlags>;
  };
}

function readWindowConfig(): Partial<RuntimeConfig> {
  if (typeof window === 'undefined') {
    return {};
  }
  const w = window as WindowWithRuntimeConfig;
  return w.__RPAPP_CUSTOMER_CONFIG__ ?? {};
}

export interface AssertApiBaseUrlSameOriginOptions {
  /** Override `import.meta.env.PROD` for hermetic Jest (G21). */
  readonly isProd?: boolean;
}

/**
 * G7 / G9 / G21 — Caddy/Vite same-origin is the cookie-auth contract: empty
 * `apiBaseUrl` (relative `/api/...`) so HttpOnly Path=/api cookies stick with
 * `credentials:'include'`. Absolute cross-origin `VITE_API_BASE_URL` (e.g. `:3015`
 * from `:3003`) hard-fails at boot in DEV and PROD — soft warn is forbidden.
 */
export function assertApiBaseUrlSameOriginContract(
  apiBaseUrl: string,
  options?: AssertApiBaseUrlSameOriginOptions,
): void {
  if (typeof window === 'undefined') {
    return;
  }
  const trimmed = apiBaseUrl.trim();
  // Empty or root-relative → browser same-origin `/api/...` via Vite/Caddy proxy.
  if (trimmed.length === 0 || trimmed.startsWith('/')) {
    return;
  }
  // options.isProd retained for hermetic Jest call sites; hard-fail is unconditional.
  void options?.isProd;
  let configured: URL;
  try {
    configured = new URL(trimmed, window.location.origin);
  } catch {
    throw new Error(
      '[rpapp-customer] Invalid VITE_API_BASE_URL — cookie auth may fail. Use empty apiBaseUrl (same-origin via Caddy/Vite proxy).',
    );
  }
  if (configured.origin === window.location.origin) {
    return;
  }
  throw new Error(
    '[rpapp-customer] VITE_API_BASE_URL is cross-origin — HttpOnly refresh cookies will not work. Production contract is same-origin: leave apiBaseUrl empty and terminate API under the customer origin (Caddy / Vite proxy).',
  );
}

function readEnv(): Partial<RuntimeConfig> & { featureFlags?: Partial<CustomerFeatureFlags> } {
  return {
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
    passkeyLoginEnabled:
      import.meta.env.VITE_PASSKEY_LOGIN_ENABLED === 'true' ? true : undefined,
    buildVersion: import.meta.env.VITE_BUILD_VERSION,
    consentIngestGateEnabled:
      import.meta.env.VITE_CONSENT_INGEST_GATE_ENABLED === 'true'
        ? true
        : undefined,
    sessionRefreshIntervalMs:
      import.meta.env.VITE_SESSION_REFRESH_INTERVAL_MS !== undefined
        ? Number(import.meta.env.VITE_SESSION_REFRESH_INTERVAL_MS)
        : undefined,
    customerIdleTimeoutMs:
      import.meta.env.VITE_CUSTOMER_IDLE_TIMEOUT_MS !== undefined
        ? Number(import.meta.env.VITE_CUSTOMER_IDLE_TIMEOUT_MS)
        : undefined,
    customerAbsoluteSessionMaxMs:
      import.meta.env.VITE_CUSTOMER_ABSOLUTE_SESSION_MAX_MS !== undefined
        ? Number(import.meta.env.VITE_CUSTOMER_ABSOLUTE_SESSION_MAX_MS)
        : undefined,
    sentryDsn: import.meta.env.VITE_SENTRY_DSN,
    sentryEnvironment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    featureFlags: {
      customerAccountActiveSessionsV1:
        import.meta.env.VITE_CUSTOMER_ACCOUNT_ACTIVE_SESSIONS_V1 === 'true',
      customerWalletPaymentsV1_1:
        import.meta.env.VITE_CUSTOMER_WALLET_PAYMENTS_V1_1 === 'true',
      customerKuponyEnabled:
        import.meta.env.VITE_CUSTOMER_KUPONY_ENABLED === 'true',
      customerFavoritesNavEnabled: parseCustomerBootFlagEnv(
        import.meta.env.VITE_CUSTOMER_FAVORITES_NAV_ENABLED,
      ),
      customerShopsNavEnabled: parseCustomerBootFlagEnv(
        import.meta.env.VITE_CUSTOMER_SHOPS_NAV_ENABLED,
      ),
      customerLoyaltyNavEnabled: parseCustomerBootFlagEnv(
        import.meta.env.VITE_CUSTOMER_LOYALTY_NAV_ENABLED,
      ),
      customerBrowseNavEnabled: parseCustomerBrowseNavBootEnv(
        import.meta.env.VITE_CUSTOMER_BROWSE_NAV_ENABLED,
      ),
      customerScanNavEnabled: parseCustomerBrowseNavBootEnv(
        import.meta.env.VITE_CUSTOMER_SCAN_NAV_ENABLED,
      ),
      customerGiftSurfacesMasterEnabled:
        import.meta.env.VITE_CUSTOMER_GIFT_SURFACES_MASTER_ENABLED === 'true',
      customerGiftDonateMenuEnabled:
        import.meta.env.VITE_CUSTOMER_GIFT_DONATE_MENU_ENABLED === 'true',
      customerGiftRecurringDonationsEnabled:
        import.meta.env.VITE_CUSTOMER_GIFT_RECURRING_DONATIONS_ENABLED ===
        'true',
      customerGiftMyDonationsEnabled:
        import.meta.env.VITE_CUSTOMER_GIFT_MY_DONATIONS_ENABLED === 'true',
    },
  };
}

export function loadRuntimeConfig(): RuntimeConfig {
  const fromWindow = readWindowConfig();
  const fromEnv = readEnv();

  const apiBaseUrlRaw = fromWindow.apiBaseUrl ?? fromEnv.apiBaseUrl;
  const apiBaseUrl =
    apiBaseUrlRaw !== undefined && apiBaseUrlRaw !== null
      ? String(apiBaseUrlRaw).trim()
      : '';
  const passkeyLoginEnabled =
    fromWindow.passkeyLoginEnabled ?? fromEnv.passkeyLoginEnabled ?? false;
  const buildVersion =
    fromWindow.buildVersion ?? fromEnv.buildVersion ?? 'dev';
  const consentIngestGateEnabled =
    fromWindow.consentIngestGateEnabled ??
    fromEnv.consentIngestGateEnabled ??
    false;
  const sessionRefreshIntervalMs =
    fromWindow.sessionRefreshIntervalMs ??
    fromEnv.sessionRefreshIntervalMs ??
    15 * 60_000;
  const customerIdleTimeoutMs =
    fromWindow.customerIdleTimeoutMs ??
    fromEnv.customerIdleTimeoutMs ??
    1_800_000;
  const customerAbsoluteSessionMaxMs =
    fromWindow.customerAbsoluteSessionMaxMs ??
    fromEnv.customerAbsoluteSessionMaxMs ??
    604_800_000;

  const sentryDsn = fromWindow.sentryDsn ?? fromEnv.sentryDsn;
  const sentryEnvironment =
    fromWindow.sentryEnvironment ?? fromEnv.sentryEnvironment ?? 'development';

  assertApiBaseUrlSameOriginContract(apiBaseUrl);

  const featureFlags = resolveCustomerFeatureFlags({
    ...fromEnv.featureFlags,
    ...fromWindow.featureFlags,
  });

  return {
    apiBaseUrl,
    passkeyLoginEnabled,
    buildVersion,
    consentIngestGateEnabled,
    sessionRefreshIntervalMs,
    customerIdleTimeoutMs,
    customerAbsoluteSessionMaxMs,
    featureFlags,
    ...(sentryDsn !== undefined && sentryDsn.length > 0 ? { sentryDsn } : {}),
    sentryEnvironment,
  };
}
